<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import useUserContext from '../use-user-context';
import { createSecureLoginLocalRedirectUrl } from '../createLocalRedirectUrl';
import useAuthentication from '../use-authentication';

const router = useRouter();
const route = useRoute();
const { authenticateWithCode, createLoginLink } = useAuthentication();
const userContext = useUserContext();

type Props = {
  clientId: string;
  serviceName: string;
  basePath?: string;
  onBeforeExchangingCode?: () => Promise<void> | void;
};
const props = defineProps<Props>();

const goAfterSecureAuth = () => {
  const { redirect } = route.query;
  if (route.query.redirect) {
    try {
      const url = new URL(`${redirect}`, window.location.origin);
      const basePath = props.basePath;
      const path = basePath ? url.pathname.replace(basePath, '/') : url.pathname;
      router.push({
        // replaced with base path since router path works relative to base path.
        path,
        query: Object.fromEntries(url.searchParams.entries()),
        replace: true,
      });
    } catch {
      // not interested in possible bad URLs
    }
    return;
  }

  router.push({
    path: '/',
    query: {},
  });
};

const checkAuthentication = async () => {
  const { code, redirect, reauthenticate } = route.query;

  const redirectUrl = (Array.isArray(redirect) ? redirect[0] : redirect)?.toString();

  const currentUrl = new URL(window.location.origin);
  if (currentUrl.searchParams.get('reauthenticate') === 'auto') {
    currentUrl.searchParams.delete('reauthenticate');
  }

  // always use code if there is
  if (code) {
    /*
    As in `Login.vue`, the stored tokens are not cleared before the exchange. Unlike there, they are
    not cleared when it fails either: this flow re-authenticates the user who is already signed in,
    so a failed attempt should leave their existing session alone.
     */
    await props.onBeforeExchangingCode?.();
    await authenticateWithCode(
      props.serviceName,
      code as string,
      props.clientId,
      undefined,
      undefined,
      createSecureLoginLocalRedirectUrl(currentUrl.toString(), props.basePath, redirectUrl),
    );
    goAfterSecureAuth();
    return;
  }

  // `reauthenticate=auto` forces a re-login, so skip the token check entirely
  const reLogin = reauthenticate === 'auto' || !(await userContext.isLoggedInAsync());

  if (reLogin) {
    await Promise.all([props.onBeforeExchangingCode?.(), userContext.clearTokens()]);
    const loginLink = createLoginLink({
      appName: props.serviceName,
      clientId: props.clientId,
      reauthenticate: 'auto',
      localRedirectUrl: createSecureLoginLocalRedirectUrl(currentUrl.toString(), props.basePath, redirectUrl),
    });

    window.location.href = loginLink;
  }

  return;
};

checkAuthentication();
</script>
<template>
  <span>
    <slot> </slot>
  </span>
</template>

<style scoped></style>
